import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type ReferralStatus = 'pending' | 'converted' | 'dismissed'

const STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: 'Pending',
  converted: 'Converted',
  dismissed: 'Dismissed',
}

const STATUS_STYLES: Record<ReferralStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  converted: 'bg-green-100 text-green-700',
  dismissed: 'bg-gray-100 text-gray-500',
}

export const ReferralsPage = () => {
  const [candidateName, setCandidateName] = useState('')
  const [candidateEmail, setCandidateEmail] = useState('')
  const [selectedJobId, setSelectedJobId] = useState<Id<'jobs'> | undefined>(
    undefined,
  )
  const [note, setNote] = useState('')

  const { data: referrals = [] } = useQuery(
    convexQuery(api.features.candidates.referrals.listMyReferrals, {}),
  )

  const { data: jobs = [] } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyJobs, {}),
  )

  const { mutateAsync: submitReferral, isPending: isSubmitting } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.referrals.submitMyReferral,
    ),
  })

  const { mutateAsync: convertReferral } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.referrals.convertMyReferralToCandidate,
    ),
  })

  const { mutateAsync: dismissReferral } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.referrals.dismissMyReferral,
    ),
  })

  const handleSubmitReferral = async (): Promise<void> => {
    if (!candidateName.trim() || !candidateEmail.trim() || !selectedJobId)
      return
    await submitReferral({
      candidateName: candidateName.trim(),
      candidateEmail: candidateEmail.trim(),
      jobId: selectedJobId,
      note: note.trim() || undefined,
    })
    setCandidateName('')
    setCandidateEmail('')
    setSelectedJobId(undefined)
    setNote('')
  }

  const formatDate = (timestampMs: number): string =>
    new Date(timestampMs).toLocaleDateString()

  const pendingReferrals = referrals.filter((r) => r.status === 'pending')
  const processedReferrals = referrals.filter((r) => r.status !== 'pending')

  return (
    <div className="flex h-full w-full flex-col px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-8">
        <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <h2 className="text-sm font-medium text-primary">
            Submit a referral
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="Candidate name"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
            />
            <Input
              placeholder="Candidate email"
              type="email"
              value={candidateEmail}
              onChange={(e) => setCandidateEmail(e.target.value)}
            />
          </div>
          <Select
            value={selectedJobId ?? ''}
            onValueChange={(value) => setSelectedJobId(value as Id<'jobs'>)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select job..." />
            </SelectTrigger>
            <SelectContent>
              {jobs.map((job) => (
                <SelectItem key={job._id} value={job._id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <textarea
            className="resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
            rows={2}
            placeholder="Optional note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div>
            <Button
              size="sm"
              onClick={() => void handleSubmitReferral()}
              disabled={
                !candidateName.trim() ||
                !candidateEmail.trim() ||
                !selectedJobId ||
                isSubmitting
              }
            >
              {isSubmitting ? 'Submitting...' : 'Submit referral'}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-primary">
            Referral queue
            {pendingReferrals.length > 0 && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary/60">
                {pendingReferrals.length}
              </span>
            )}
          </h2>

          {pendingReferrals.length === 0 ? (
            <p className="text-sm text-primary/40">No pending referrals.</p>
          ) : (
            <div className="overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary text-left">
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Candidate
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Job
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Note
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Date
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {pendingReferrals.map((referral) => {
                    const job = jobs.find((j) => j._id === referral.jobId)
                    return (
                      <tr
                        key={referral._id}
                        className="border-b border-border last:border-0 hover:bg-secondary/50"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium">
                            {referral.candidateName}
                          </p>
                          <p className="text-xs text-primary/60">
                            {referral.candidateEmail}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-primary/80">
                          {job?.title ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-primary/60">
                          {referral.note ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-primary/60">
                          {formatDate(referral.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void convertReferral({
                                  referralId: referral._id,
                                })
                              }
                            >
                              Convert to candidate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void dismissReferral({
                                  referralId: referral._id,
                                })
                              }
                            >
                              Dismiss
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {processedReferrals.length > 0 && (
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-medium text-primary/60">
              Processed referrals
            </h2>
            <div className="overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary text-left">
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Candidate
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Job
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Status
                    </th>
                    <th className="px-4 py-3 font-medium text-primary/70">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {processedReferrals.map((referral) => {
                    const job = jobs.find((j) => j._id === referral.jobId)
                    return (
                      <tr
                        key={referral._id}
                        className="border-b border-border last:border-0"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium">
                            {referral.candidateName}
                          </p>
                          <p className="text-xs text-primary/60">
                            {referral.candidateEmail}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-primary/80">
                          {job?.title ?? '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[referral.status as ReferralStatus] ?? ''}`}
                          >
                            {STATUS_LABELS[referral.status as ReferralStatus] ??
                              referral.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-primary/60">
                          {formatDate(referral.createdAt)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
